import { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { OTP } from './OTPInput';

import { handleConfirmNewPassword } from '../../../api/Auth';
import { passwordRequirements, validationMessage } from '../../../configure/validationConfig';
import CustomLabelInput from '../../../components/Custom/CustomLabelInput';
import ResendResetCode from './ResendResetCode';
import SnackbarComponent from '../../../components/CustomBarComponent';

import {
    Button,
    FormHelperText,
    Grid,
    Link,
    Stack,
    Typography
} from "@mui/material";

import { useTheme } from '@mui/material/styles';

import * as Yup from "yup";
import { Formik } from "formik";

import { Link as RouterLink, useNavigate } from 'react-router-dom';

const display_text = "We've sent a 6-digit confirmation email to your email. Please enter the code in below box to verify your email."

const passwordSchema = Yup.string()
    .max(30)
    .matches(passwordRequirements[0].test)
    .matches(passwordRequirements[1].test)
    .matches(passwordRequirements[2].test)
    .matches(passwordRequirements[3].test)
    .matches(passwordRequirements[4].test)
    .matches(passwordRequirements[5].test)
    .required(validationMessage.ENTER_PASSWORD);


const VerifyCode = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { email = " " } = location?.state || {};

    const theme = useTheme();
    const LOADING_COLOR = theme.palette.action.disabled;
    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState('');
    const [isCodeMismatch, setIsCodeMismatch] = useState(false);
    const [isCodeEmpty, setIsCodeEmpty] = useState(false);

    const OTP_LENGTH = 6

    const handleChange = (newOtp) => {
      setOtp(newOtp);
    };

    const [snackbarState, setSnackbarState] = useState({
        open: false,
        message: "",
    }); 

    useEffect(()=>{
        if(otp.length === OTP_LENGTH){
            setIsCodeEmpty(false)
        }else if(otp.length === 1){
            setIsCodeEmpty(true);
        }
    },[otp])

    return (
        <>
            <SnackbarComponent
                snackbarState={snackbarState}
                setSnackbarState={setSnackbarState}
            />
            <Formik
                initialValues={{
                    password: "",
                    confirmPassword: "",
                    submit: null,
                }}
                validationSchema={Yup.object().shape({
                    password: passwordSchema,
                    confirmPassword: Yup.string()
                        .oneOf([Yup.ref('password'), null], "password mismatch")
                        .required("confirm password is required"),
                })}
                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    setStatus({ success: false });
                    setLoading(true);

                    if (otp.length === OTP_LENGTH && values.password) {
                        setLoading(true);
                        try{
                            const isConfirmed = await handleConfirmNewPassword(email, otp, values.password);
                            if(isConfirmed === "SUCCESS"){
                                navigate('/reset_success');
                            } else {
                                setSnackbarState({ open: true, message: "Password reset confirmation failed." });
                            }
                        }catch(error){
                            setLoading(false);
                            if(error.code === "CodeMismatchException"){
                                setIsCodeMismatch(true);
                            }else{
                                setSnackbarState({ open: true, message: "Error: " + error.code + error.message || "Error with reset password "}); 
                            }
                        }
                    }

                    setIsCodeEmpty(true);
                    setLoading(false);

                }}
            >
                {({
                    errors,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values,
                }) => (
                    <form noValidate onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <div style={{ textAlign: 'left', marginLeft: '25px', fontSize: '12px' , color: '#535F70', marginTop: '5px'}}> 
                               {display_text}
                            </div>
                            {isCodeMismatch && 
                                <Grid item xs={12}>
                                    <Stack spacing={1}>
                                        <Typography style={{textIndent: '10px', background: '#FFB4AB', height: '40px', alignContent:'center', padding: '5px', borderRadius: '4px'}}>
                                            Incorrect verification code.
                                        </Typography>
                                    </Stack>
                                </Grid>
                            }
                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <Typography style={{color : '#42474E', fontSize: '12px', marginTop: "-20px" }}>
                                        Verification Code
                                    </Typography>
                                    <div style={{display: 'flex', justifyContent: 'center'}}>
                                        <OTP
                                            length={6}
                                            value={otp}
                                            onChange={handleChange}
                                            separator=""
                                            error={isCodeEmpty}
                                        />
                                    </div>
                                </Stack>
                                {isCodeEmpty && 
                                    <Typography style={{color: '#BA1A1A', fontSize: '12px', marginTop: '5px'}}>
                                        Please enter verification code
                                    </Typography>
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <CustomLabelInput
                                        label="New password"
                                        name="password"
                                        type="password"
                                        disabled={isSubmitting}
                                        showPasswordToggle={true}
                                        placeholder="Enter your password"
                                        showRules={true}
                                    />
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                <CustomLabelInput
                                    label="Confirm password"
                                    name="confirmPassword"
                                    type="password"
                                    disabled={isSubmitting}
                                    showPasswordToggle={true}
                                    placeholder="Confirm password"
                                />
                                </Stack>
                            </Grid>
                            {errors.submit && (
                                <Grid item xs={12}>
                                    <FormHelperText error>{errors.submit}</FormHelperText>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Button
                                    disableElevation
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                >
                                    Reset Password
                                </Button>

                            </Grid>
                            <Grid item xs={12}>
                                <ResendResetCode email={email}/>
                            </Grid>
                            <Grid item xs={12}>
                                <Link
                                    variant="h6"
                                    component={RouterLink}
                                    to="/forget_password"
                                    disabled={loading}
                                    onClick={(e) => loading && e.preventDefault()}
                                    sx={{
                                        color: !loading ? "primary.darker" : LOADING_COLOR,
                                    }}
                                >
                                    Back
                                </Link>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Formik>
        </>
    );
}


export default VerifyCode;